// Where a widget's figures come from, as small marks in its top-right corner (Shopify, GA4, Google
// Ads, Meta, TikTok, the social platforms, HMRC). The server works out each widget's sources from its metrics, so the
// marks render with the frame, before any data arrives.
import type { ReactNode } from 'react';

const LABELS: Record<string, string> = {
  shopify: 'Shopify',
  ga4: 'Google Analytics 4',
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  tiktok_ads: 'TikTok Ads',
  snapchat_ads: 'Snapchat Ads',
  klaviyo: 'Klaviyo',
  hubspot: 'HubSpot',
  ads: 'All ad platforms',
  hmrc: 'HMRC exchange rates',
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  threads: 'Threads',
  linkedin: 'LinkedIn',
  pinterest: 'Pinterest',
  x: 'X',
  snapchat: 'Snapchat',
  social: 'All social platforms',
};

const MARKS: Record<string, ReactNode> = {
  shopify: (
    <>
      <path d="M5 8.2h14l-1.15 12a1.2 1.2 0 0 1-1.2 1.1H7.35a1.2 1.2 0 0 1-1.2-1.1z" fill="#95BF47" />
      <path d="M9 8.2V6.6a3 3 0 0 1 6 0v1.6" fill="none" stroke="#5E8E3E" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M14 12.4c-.5-.5-1.2-.7-1.9-.7-1 0-1.7.5-1.7 1.2 0 1.6 3.7 1 3.7 2.9 0 .9-.9 1.5-2.1 1.5-.8 0-1.6-.3-2.1-.8" fill="none" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
    </>
  ),
  ga4: (
    <>
      <rect x="15" y="3" width="5" height="18" rx="2.5" fill="#E37400" />
      <rect x="9.5" y="9" width="5" height="12" rx="2.5" fill="#F9AB00" />
      <circle cx="6.5" cy="18.5" r="2.5" fill="#F9AB00" />
    </>
  ),
  google_ads: (
    <>
      <line x1="5.2" y1="18.3" x2="10.6" y2="5.8" stroke="#FBBC04" strokeWidth="4.6" strokeLinecap="round" />
      <line x1="13.4" y1="5.8" x2="18.8" y2="18.3" stroke="#4285F4" strokeWidth="4.6" strokeLinecap="round" />
      <circle cx="5.2" cy="18.3" r="2.6" fill="#34A853" />
    </>
  ),
  meta_ads: (
    <path
      d="M3 12c0-3 1.6-5 3.6-5 3.6 0 6.2 10 10.8 10 2 0 3.6-2 3.6-5s-1.6-5-3.6-5C12.8 7 10.2 17 6.6 17 4.6 17 3 15 3 12z"
      fill="none"
      stroke="#0866FF"
      strokeWidth="2.3"
    />
  ),
  tiktok_ads: (
    <>
      {[
        ['#25F4EE', 'translate(-0.8 -0.6)'],
        ['#FE2C55', 'translate(0.8 0.6)'],
        ['currentColor', undefined],
      ].map(([color, transform]) => (
        <g key={color} transform={transform} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13.5 3.5v11a3.4 3.4 0 1 1-3-3.4" />
          <path d="M13.5 3.5c.4 2.3 2 3.9 4.6 4.1" />
        </g>
      ))}
    </>
  ),
  ads: (
    <g fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" strokeLinecap="round">
      <path d="M4 10v4h3l6 4V6l-6 4H4z" />
      <path d="M16.5 9a4.2 4.2 0 0 1 0 6" />
    </g>
  ),
  facebook: (
    <>
      <circle cx="12" cy="12" r="10" fill="#0866FF" />
      <path d="M13.4 19.5v-6h2l.3-2.4h-2.3V9.6c0-.7.2-1.2 1.2-1.2h1.2V6.3a16 16 0 0 0-1.8-.1c-1.8 0-3 1.1-3 3.1v1.8H9v2.4h2v6z" fill="#fff" />
    </>
  ),
  instagram: (
    <>
      <defs>
        <linearGradient id="src-ig" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#FEDA75" />
          <stop offset=".35" stopColor="#FA7E1E" />
          <stop offset=".65" stopColor="#D62976" />
          <stop offset="1" stopColor="#4F5BD5" />
        </linearGradient>
      </defs>
      <rect x="3" y="3" width="18" height="18" rx="5.5" fill="url(#src-ig)" />
      <circle cx="12" cy="12" r="4" fill="none" stroke="#fff" strokeWidth="1.9" />
      <circle cx="17" cy="7" r="1.2" fill="#fff" />
    </>
  ),
  youtube: (
    <>
      <rect x="2" y="5.5" width="20" height="13" rx="4" fill="#FF0000" />
      <path d="M10 9.2v5.6l5-2.8z" fill="#fff" />
    </>
  ),
  threads: (
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="5" fill="#000" />
      <text x="12" y="16.6" textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">
        @
      </text>
    </>
  ),
  linkedin: (
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="3.5" fill="#0A66C2" />
      <text x="12" y="16.8" textAnchor="middle" fontSize="11.5" fontWeight="700" fill="#fff">
        in
      </text>
    </>
  ),
  pinterest: (
    <>
      <circle cx="12" cy="12" r="10" fill="#E60023" />
      <text x="12" y="17.2" textAnchor="middle" fontSize="14" fontWeight="700" fill="#fff">
        P
      </text>
    </>
  ),
  x: (
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="5" fill="#000" />
      <path d="M7.5 7l9 10M16.5 7l-9 10" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
    </>
  ),
  snapchat: (
    <>
      <rect x="2.5" y="2.5" width="19" height="19" rx="5" fill="#FFFC00" />
      <path
        d="M12 5.2c-2.6 0-4 1.9-4 4.1v1.8l-1.3.4c-.3.1-.3.4 0 .6l1.2.7c-.5 1.4-1.5 2.4-2.9 2.8.2.5 1 .7 1.9.9.1.5.2.8.6.8.5 0 1.1-.2 1.9-.1.7.2 1.5 1.1 2.6 1.1s1.9-.9 2.6-1.1c.8-.1 1.4.1 1.9.1.4 0 .5-.3.6-.8.9-.2 1.7-.4 1.9-.9-1.4-.4-2.4-1.4-2.9-2.8l1.2-.7c.3-.2.3-.5 0-.6l-1.3-.4V9.3c0-2.2-1.4-4.1-4-4.1z"
        fill="#fff"
        stroke="#000"
        strokeWidth=".9"
        strokeLinejoin="round"
      />
    </>
  ),
  social: (
    <g fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
      <circle cx="18" cy="5" r="2.6" />
      <circle cx="6" cy="12" r="2.6" />
      <circle cx="18" cy="19" r="2.6" />
      <path d="M8.3 13.3l7.4 4.4M15.7 6.3l-7.4 4.4" />
    </g>
  ),
  hmrc: (
    <>
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <text x="12" y="16.3" textAnchor="middle" fontSize="12" fontWeight="700" fill="currentColor">
        £
      </text>
    </>
  ),
};

// Klaviyo: its flag mark, white on black.
MARKS.klaviyo = (
  <>
    <rect x="2.5" y="2.5" width="19" height="19" rx="5" fill="#000" />
    <path d="M7 6.5h10.5l-3 4.25 3 4.25H8.75V18.5H7z" fill="#fff" />
  </>
);

// HubSpot: its sprocket, white on orange.
MARKS.hubspot = (
  <>
    <rect x="2.5" y="2.5" width="19" height="19" rx="5" fill="#FF7A59" />
    <circle cx="14.5" cy="13.5" r="3" fill="none" stroke="#fff" strokeWidth="1.8" />
    <path d="M14.5 10.5V7.25M12.3 15.7l-3.3 2.8M8 7.5l4.3 3.8" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="14.5" cy="6.6" r="1.3" fill="#fff" />
    <circle cx="7.4" cy="6.9" r="1.2" fill="#fff" />
  </>
);

// TikTok's organic account and its ads share the brand mark.
MARKS.tiktok = MARKS.tiktok_ads;
MARKS.snapchat_ads = MARKS.snapchat;

export function sourceLabel(id: string): string {
  return LABELS[id] ?? id;
}

export function SourceIcons({ sources }: { sources?: string[] }) {
  const known = (sources ?? []).filter((id) => MARKS[id]);
  if (known.length === 0) return null;
  const names = known.map(sourceLabel).join(', ');
  return (
    <span className="w-sources" role="img" aria-label={`Data from ${names}`} title={`Data from ${names}`}>
      {known.map((id) => (
        <span key={id} className="src-icon" data-source={id}>
          <svg viewBox="0 0 24 24" aria-hidden focusable="false">
            {MARKS[id]}
          </svg>
        </span>
      ))}
    </span>
  );
}
